/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 06-09-2026
 *
 * Het opslagformaat van een model: schrijven (`schrijf`, voorheen `modelData()` in de
 * AngularJS-modelview-controller, golf a2 stap 1) en lezen (`lees`, de deserialisatie uit
 * `_drawGraph()`, stap 2). Beide kanten van hetzelfde contract staan bewust in één bestand: het
 * formaat is productiedata. Wat hier geschreven wordt moet door `lees` én door elke eerdere versie
 * van de applicatie gelezen kunnen worden, en omgekeerd.
 *
 * Geen Angular-service maar een gewone klasse in eigendom van `DlModel` (het patroon van
 * `SimResultaatTekenaar`): `DlModel` is de enige aanroeper en biedt `modelData()`/`tekenGraaf()` als
 * publieke ingang. Puur: hier wordt alleen de graaf opgebouwd en de documentstate gezet — geen
 * engine, geen canvas-UI (labels, zoom, selectie, animatielock) en geen melding. Dat doen `DlModel`
 * (de tekenmelding) en `DlModelCanvasComponent` (de UI) eromheen.
 */
import type {Core, ElementDefinition, NodeSingular, Position} from 'cytoscape';
import {elements} from 'dynalearn-common/lib/elements';
import type {DlModel, NormDocument} from './dl-model';
import type {GlobalModelcanvasService} from './global-modelcanvas.service';
import type {CondtagService, CondtagState} from '../../services/condtag.service';
import type {ModelId} from '../backend/api.service';

/**
 * De data van één modelelement, zoals cytoscape hem draagt en het opslagformaat hem bewaart:
 * altijd een id en een type, en verder wat het elementtype nodig heeft (`nodename`, `parentId`,
 * `from`/`to`, `condtag`/`condstate`, `ballon`, …). `schrijf` schrijft `node.data()` ongefilterd
 * weg, dus dit is letterlijk wat er in de graaf staat.
 */
export interface ModelElementData {
    id: string;
    type: string;
    [key: string]: unknown;
}

/**
 * De metadata die de `OpslagService` bij het opslaan aan de data toevoegt (`addMeta`). Hij hoort
 * niet bij wat `schrijf` schrijft — een snapshot heeft hem dus niet — maar een opgeslagen model
 * heeft hem altijd, en dat maakt hem het kenmerk waaraan `isVolledigDocument` een compleet
 * opgeslagen model herkent.
 */
export interface ModelMeta {
    /** Het model-id (een uuid), of `null` als het model nog nooit is opgeslagen. */
    id: ModelId | null;
    /** De titel van het model. */
    name: string;
    /** Het normmodel waarop dit model gebouwd is, of `false`. */
    norm: NormDocument | false;
    /** De notatieversie van het model; `addMeta` zet altijd 1. */
    notationversion: number;
}

/**
 * Het opslagformaat van een model: wat er naar het backend gaat, in de undo-buffer belandt en als
 * snapshot aan een actielogregel hangt. De sleutelnamen zijn het contract met opgeslagen
 * productiedata en met het backend en liggen daarmee vast.
 *
 * Dit beschrijft wat `schrijf` schrijft, plus het `meta`-blok dat de `OpslagService` erbij zet. Een
 * snapshot voegt er de simulatie-, norm- en selectiestand aan toe; zie `Modelsnapshot` in de
 * `OpslagService` (sinds golf a5 stap 7 `maakSnapshot`).
 *
 * Oudere documenten missen velden: `condtags` en `condtag` bestaan sinds 21-03-2021, de vier
 * toon-vlaggen sinds golf d2. `lees` valt dan terug op de defaults.
 */
export interface ModelDocument {
    /** De metadata van een opgeslagen model; ontbreekt in een snapshot. Zie `ModelMeta`. */
    meta?: ModelMeta;
    /** De bewaarde viewstand: alleen de zoomfactor, op twee decimalen. */
    cy: {zoom: number};
    /** Per element-id de grafische node-json (positie e.d.), zonder `classes` en `data`. */
    nodes: Record<string, Record<string, unknown>>;
    /** Het logische model: de data per element-id, en per elementtype de ids. */
    model: {
        elements: Record<string, ModelElementData>;
        types: Record<string, string[]>;
    };
    /** De conditionele tags van dit model, met per tag of hij zichtbaar is. */
    condtags: Record<string, boolean>;
    /** De actieve conditieset, of `null`. */
    condtag: CondtagState | null;
    /** Toont de noodzakelijke bouwsuggesties (default `true`). */
    bsToonNoodzakelijk: boolean;
    /** Toont de gewenste bouwsuggesties (default `false`). */
    bsToonGewenst: boolean;
    /** Toont de normfeedback (default `true`). */
    normToon: boolean;
    /** Toont de simulatiefeedback (default `false`). */
    simToon: boolean;
}

export class ModelSerialisatie {

    /**
     * @param model Het document: de graafprimitieven (`nodeClasses`, `addEdgeFromTo`, …) en de
     *   documentstate die in het formaat zit (de toon-vlaggen, het effectieve modelleerniveau).
     * @param condtagService Eigenaar van de conditionele tags, die ook in het formaat zitten.
     * @param modelcanvas Permanente eigenaar van de model-`cy`.
     */
    constructor(
        private readonly model: DlModel,
        private readonly condtagService: CondtagService,
        private readonly modelcanvas: GlobalModelcanvasService
    ) {
    }

    /**
     * Of dit een compleet opgeslagen model is: de drie dragende velden van het formaat plus de
     * `meta` die de `OpslagService` bij het opslaan toevoegt. Guard voor het herladen — een document
     * dat hier niet doorheen komt, wordt niet getekend en laat de bestaande graaf staan. Een
     * snapshot komt hier niet langs: die wordt via `tekenGraaf` getekend en heeft geen `meta`.
     */
    public isVolledigDocument(doc: ModelDocument | null | undefined): boolean {
        return !!(doc?.meta && doc.cy && doc.nodes && doc.model);
    }

    /**
     * Stelt de modeldata samen: het model als javascript-object in het opslagformaat (geen json).
     *
     * We bewaren niet zoveel: een paar algemene dingen, de elementen (nodes, geen edges) en wat er
     * nodig is om te herstellen. Dus geen `cy.json()` — de nodes zijn met opzet puur gehouden,
     * zonder versieafhankelijke lay-outinfo, en definities zitten in de scratch en gaan dus niet
     * mee. Edges en edgehandles worden bij het lezen opnieuw gegenereerd, net als de classes;
     * vergeet niet dat dynalearn-relaties zélf ook nodes zijn. Wat uit de simulatie komt slaan we
     * niet op.
     */
    public schrijf(): ModelDocument {
        const cy = this.cy;
        const doc: ModelDocument = {
            cy: {
                zoom: Math.round(cy.zoom() * 100) / 100 // 2 cijfers achter de komma
            },
            nodes: {}, // op id
            model: { // logischer model, bevat de data
                elements: {}, // elementen op id (de data)
                types: {} // per type een array met ids
            },
            // de conditiesets die er zijn, zodat ze ook undoable zijn. In modellen van vóór
            // 21-03-2021 stonden ze alleen in de data van de nodes; bij het lezen worden ze
            // daaruit aangevuld, en vanaf hier gaan ze weer als eigen veld mee.
            condtags: this.condtagService.conditioneleTags(),
            condtag: this.condtagService.condtag(),
            bsToonNoodzakelijk: this.model.bsToonNoodzakelijk,
            bsToonGewenst: this.model.bsToonGewenst,
            normToon: this.model.normToon,
            simToon: this.model.simToon
        };

        cy.nodes('.model').forEach((node) => {
            if (node.hasClass('simulation')) {
                return; // niet opslaan
            }
            const data = node.data() as ModelElementData;
            doc.model.elements[data.id] = data;
            if (!doc.model.types[data.type]) {
                doc.model.types[data.type] = [];
            }
            doc.model.types[data.type].push(data.id); // op type, maar alleen de ids
            // grafische zaken: de node-json zonder de classes en de data
            const grafisch = node.json();
            delete grafisch['classes'];
            delete grafisch['data'];
            doc.nodes[data.id] = grafisch;
        });
        return doc;
    }

    /**
     * Bouwt de graaf opnieuw op uit een document: alles wat er staat gaat weg, de conditiesets en de
     * toon-vlaggen komen uit het document (met de defaults voor oudere modellen), dan de nodes en ten
     * slotte de edges. Edges en edgehandles worden nooit uit het document gelezen maar altijd opnieuw
     * gegenereerd, net als de classes en het modelleerniveau op elke node. Het document zelf blijft
     * onaangeroerd: cytoscape neemt de `data`- en `position`-objecten van een definitie over zoals ze
     * zijn, dus die krijgen hier een kopie.
     *
     * Wat we overslaan, met een waarschuwing op de console: nodes zonder bruikbaar type, elementen
     * waarvan de parent ontbreekt en relaties waarvan een argument ontbreekt. Edgehandles in het
     * document slaan we zonder melding over: die horen er niet in te zitten en komen bij de edges
     * vanzelf terug.
     *
     * De viewstand (`doc.cy.zoom`) wordt hier bewust niet toegepast, en de selectie van elke node
     * wordt gewist: menu's mogen niet spontaan verschijnen na een redraw. Wat er verder aan het
     * canvas moet gebeuren (labels, zoom, fit) is van `DlModelCanvasComponent`.
     */
    public lees(doc: Omit<ModelDocument, 'cy'>): void {
        //geen `cy`-eis: de viewstand wordt hier niet gelezen, en een snapshot heeft hem sinds 16-09-2026 niet
        if (!(doc.nodes && doc.model)) {
            throw new Error('ModelSerialisatie.lees: onvolledig document');
        }
        const cy = this.cy;
        cy.elements().remove(); // alles weg, anders mapt cy op nieuwe elementen met hetzelfde id

        // In modellen die na 21-03-2021 zijn opgeslagen zitten de conditiesets in het document, in
        // oudere alleen in de data van de nodes; we werken voor de zekerheid met allebei (undo/redo
        // werkt alleen goed in de nieuwe opzet). De actieve conditieset komt onderaan.
        this.condtagService.zetTags(doc.condtags ?? {});
        this.model.bsToonNoodzakelijk = doc.bsToonNoodzakelijk ?? true;
        this.model.bsToonGewenst = doc.bsToonGewenst ?? false;
        this.model.normToon = doc.normToon ?? true;
        this.model.simToon = doc.simToon ?? false;

        const elementen = doc.model.elements;
        for (const [id, grafisch] of Object.entries(doc.nodes)) {
            const data = elementen[id];
            if (!(data?.type && data.type !== 'noElement')) {
                console.warn('ModelSerialisatie: node zonder type overgeslagen', id, grafisch);
                continue;
            }
            if (elements.isSubtypeOf(data.type, 'edgeHandle')) {
                continue;
            }
            if (data['parentId'] && !elementen[data['parentId'] as string]) {
                console.warn('ModelSerialisatie: element met missende parent overgeslagen', data);
                continue;
            }
            if ((data['from'] && !elementen[data['from'] as string]) || (data['to'] && !elementen[data['to'] as string])) {
                console.warn('ModelSerialisatie: relatie met missend argument overgeslagen', data);
                continue;
            }
            const node = cy.add({
                ...grafisch,
                position: {...(grafisch['position'] as Position)},
                data: {...data, mln: this.model.effectief_modelleerniveau},
                classes: this.model.nodeClasses(data.type),
                selected: false
            } as ElementDefinition) as unknown as NodeSingular;

            // afgesproken met Bert Bredeweg d.d. 7-2-2016: als één van de argumenten van een relatie
            // een calc is, blijft het geheel zichtbaar in simulate — dat vangt `nodeClasses` niet
            if (elements.isSubtypeOf(data.type, 'relation')) {
                const starttype = elementen[data['from'] as string].type;
                const eindtype = elementen[data['to'] as string].type;
                if (elements.isSubtypeOf(starttype, 'calc') || elements.isSubtypeOf(eindtype, 'calc')) {
                    node.removeClass('hide_in_simulate');
                }
            }

            // een tag die alleen in de node-data staat: nu wel bekend, maar standaard niet zichtbaar
            const condtag = data['condtag'] as string | undefined;
            if (condtag && !Object.hasOwn(this.condtagService.conditioneleTags(), condtag)) {
                this.condtagService.zetZichtbaar(condtag, false);
            }
        }

        // de edges: de lijn naar de parent (als de definitie dat wil) en de twee lijnen van een
        // relatie. `cy.nodes()` is een momentopname, dus de edgehandles die hierbij ontstaan lopen
        // niet mee.
        for (const node of cy.nodes()) {
            const nodetype = node.data('type') as string | undefined;
            if (!nodetype) {
                console.warn('ModelSerialisatie: node zonder type', node.data());
                continue;
            }
            const parentId = node.data('parentId') as string | undefined;
            if (parentId && elements.getDefinition(nodetype).connecttoparent) {
                const parent = cy.getElementById(parentId) as unknown as NodeSingular;
                // tussen elementen van een quantity space of een afgeleide onderling is het een
                // gewone lijn, anders een parent-kind-lijn
                const edgetype = this.isQsOfDerivativeElement(parent.data('type')) && this.isQsOfDerivativeElement(nodetype) ? 'normal' : 'parentchild';
                this.model.addEdgeFromTo(parent, node, edgetype, 'model');
            }
            if (elements.isSubtypeOf(nodetype, 'relation')) {
                this.model.addEdgeFromTo(cy.getElementById(node.data('from')) as unknown as NodeSingular, node, 'source', 'model');
                this.model.addEdgeFromTo(cy.getElementById(node.data('to')) as unknown as NodeSingular, node, 'target', 'model');
            }
        }

        // de actieve conditieset; of het paneel open staat is sessie-UI en blijft zoals het was
        this.condtagService.zetActieveTag(doc.condtag ?? null);
        this.model.updateConditieclasses();
        this.model.repositionEdgeHandles();
    }

    /**
     * Of het type een element van een quantity space of van een afgeleide is.
     */
    private isQsOfDerivativeElement(type: string): boolean {
        return elements.isSubtypeOf(type, 'quantity_space_element') || elements.isSubtypeOf(type, 'derivative_element');
    }

    /**
     * De actieve model-`cy`. Gooit als er (nog) geen canvas is, net als `DlModel`.
     */
    private get cy(): Core {
        const cy = this.modelcanvas.getCy();
        if (!cy) {
            throw new Error('ModelSerialisatie: geen actief modelcanvas beschikbaar');
        }
        return cy;
    }
}
